/**
 * ECDH P-256 envelope for the multi-recipient wrap pattern.
 *
 *   wrapCKForRecipient(CK, recipient_ecdh_pub):
 *     1. generate ephemeral ECDH P-256 keypair
 *     2. shared = ECDH(ephemeral_priv, recipient_pub)  -> 32 bytes
 *     3. kek   = HKDF-SHA256(shared, salt=zeros, info="omakase-vault-wrap-v1") -> 32 bytes
 *     4. wrapped = AES-KW-256(kek, CK)                                       -> 40 bytes
 *     5. emit { ephemeral_pub_jwk, wrapped }
 *
 *   unwrapCKAsRecipient(wrapped, ephemeral_pub_jwk, my_ecdh_priv):
 *     1. shared = ECDH(my_priv, ephemeral_pub)
 *     2. kek   = HKDF-SHA256(...) [same params]
 *     3. CK    = AES-KW unwrap
 *
 * Trust note: the ephemeral pub is plaintext alongside the wrapped key on
 * the server. A malicious server can swap them but the auth path is closed
 * elsewhere (AAD on the item ciphertext) so a swap just yields a wrong CK
 * that fails AES-GCM auth at decrypt time.
 */

import { aesKwUnwrap, aesKwWrap, importAesKwKey } from "./aes.js";
import { asBrand } from "../types/index.js";
import type {
  EcdhPrivJwk,
  EcdhPubJwk,
  RawSymmetricKey,
  WrappedKeyBytes,
} from "../types/index.js";

/** HKDF salt for the wrap kek derivation. Zero-filled by construction. */
const HKDF_SALT = new Uint8Array(32);
/** HKDF info string. Bumped if the wrap derivation scheme ever changes. */
const HKDF_INFO = new TextEncoder().encode("omakase-vault-wrap-v1");

// =============================================================================
// Keypair generation + JWK encode/decode
// =============================================================================

export interface EcdhKeypair {
  readonly pubJwk: EcdhPubJwk;
  readonly privJwk: EcdhPrivJwk;
}

/** Generate a new P-256 ECDH keypair and return both as JWK strings. */
export async function generateEcdhKeypair(): Promise<EcdhKeypair> {
  const pair = await crypto.subtle.generateKey(
    { name: "ECDH", namedCurve: "P-256" },
    true,
    ["deriveBits"],
  );
  const pubJwk = await crypto.subtle.exportKey("jwk", pair.publicKey);
  const privJwk = await crypto.subtle.exportKey("jwk", pair.privateKey);
  return {
    pubJwk: JSON.stringify(pubJwk) as EcdhPubJwk,
    privJwk: JSON.stringify(privJwk) as EcdhPrivJwk,
  };
}

async function importEcdhPub(pubJwk: EcdhPubJwk): Promise<CryptoKey> {
  const jwk = JSON.parse(pubJwk) as JsonWebKey;
  return crypto.subtle.importKey(
    "jwk",
    jwk,
    { name: "ECDH", namedCurve: "P-256" },
    false,
    [],
  );
}

async function importEcdhPriv(privJwk: EcdhPrivJwk): Promise<CryptoKey> {
  const jwk = JSON.parse(privJwk) as JsonWebKey;
  return crypto.subtle.importKey(
    "jwk",
    jwk,
    { name: "ECDH", namedCurve: "P-256" },
    false,
    ["deriveBits"],
  );
}

// =============================================================================
// HKDF over the ECDH shared secret -> AES-KW kek
// =============================================================================

async function deriveWrapKek(
  myPriv: CryptoKey,
  theirPub: CryptoKey,
): Promise<CryptoKey> {
  // 1. shared bits via ECDH
  const sharedBits = await crypto.subtle.deriveBits(
    { name: "ECDH", public: theirPub },
    myPriv,
    256, // bits
  );
  // 2. HKDF derive 32 bytes of AES-KW key material
  const hkdfKey = await crypto.subtle.importKey(
    "raw",
    sharedBits,
    "HKDF",
    false,
    ["deriveBits"],
  );
  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: HKDF_SALT as unknown as ArrayBuffer,
      info: HKDF_INFO as unknown as ArrayBuffer,
    },
    hkdfKey,
    256, // bits
  );
  return importAesKwKey(asBrand<RawSymmetricKey>(new Uint8Array(derivedBits)));
}

// =============================================================================
// Wrap / unwrap a content key (CK) to a recipient
// =============================================================================

export interface WrappedForRecipient {
  readonly ephemeralPubJwk: EcdhPubJwk;
  readonly wrappedCk: WrappedKeyBytes;
}

/**
 * Wrap a 32-byte content key for one recipient via ECDH + HKDF + AES-KW.
 * The ephemeral keypair is fresh per wrap operation; its private key is
 * dropped immediately after.
 */
export async function wrapCkForRecipient(
  ck: RawSymmetricKey,
  recipientPubJwk: EcdhPubJwk,
): Promise<WrappedForRecipient> {
  if (ck.length !== 32) {
    throw new RangeError(
      `wrapCkForRecipient: CK must be 32 bytes (got ${ck.length})`,
    );
  }
  const ephemeral = await crypto.subtle.generateKey(
    { name: "ECDH", namedCurve: "P-256" },
    true,
    ["deriveBits"],
  );
  const ephPubJwk = JSON.stringify(
    await crypto.subtle.exportKey("jwk", ephemeral.publicKey),
  ) as EcdhPubJwk;

  const recipientPub = await importEcdhPub(recipientPubJwk);
  const kek = await deriveWrapKek(ephemeral.privateKey, recipientPub);
  const wrapped = await aesKwWrap(kek, ck);

  return { ephemeralPubJwk: ephPubJwk, wrappedCk: wrapped };
}

/**
 * Unwrap a content key using the recipient's private key.
 * Throws on tamper / wrong key / wrong ephemeral pub.
 */
export async function unwrapCkAsRecipient(
  wrappedCk: WrappedKeyBytes,
  ephemeralPubJwk: EcdhPubJwk,
  myEcdhPrivJwk: EcdhPrivJwk,
): Promise<RawSymmetricKey> {
  const myPriv = await importEcdhPriv(myEcdhPrivJwk);
  const ephPub = await importEcdhPub(ephemeralPubJwk);
  const kek = await deriveWrapKek(myPriv, ephPub);
  return aesKwUnwrap(kek, wrappedCk);
}
